/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { TryOnMode, useZakeke } from 'zakeke-configurator-react';
import useStore from '../../Store';
import { QuestionDialog, useDialogManager } from '../dialogs/Dialogs';
import NftDialog, { NftForm } from '../dialogs/NftDialog';
import { T } from '../../Helpers';
import { AddToCartButton, removeItemsForStyle } from '../Atomic';
import useDropdown from '../../hooks/useDropdown';
import { PriceContainer } from '../Layout/LayoutStyles';
import styled from 'styled-components';

interface MenuFooterProps {
  viewFooter: any;
  isUndo?: () => void;
  isRedo?: () => void;
  setOpenGroup?: (value: number | null) => void;
}

const PriceInfoTextContainer = styled.div`
  font-size: 14px;
`;

const MenuFooter: React.FC<MenuFooterProps> = ({ viewFooter, isUndo, isRedo, setOpenGroup }) => {
  const { showDialog, closeDialog } = useDialogManager();
  const addToCartButtonRef = useRef<HTMLButtonElement>(null);
  const [disableButtonsByVisibleMessages, setDisableButtonsByVisibleMessages] = useState(false);
  const [openOutOfStockTooltip, closeOutOfStockTooltip] = useDropdown();
  const {
    isAddToCartLoading,
    addToCart,
    price,
    useLegacyScreenshot,
    setQuantity,
    product,
    isMandatoryPD,
    quantity,
    nftSettings,
    eventMessages,
    isOutOfStock,
    visibleEventMessages,
    sellerSettings,
    reset,
  } = useZakeke();
  const {
    priceFormatter,
    pdValue,
    setIsPDStartedFromCart,
    isDraftEditor,
    isEditorMode,
    isViewerMode,
    tryOnRef,
    setTryOnMode,
  } = useStore();
  const [quantityValue, setQuantityValue] = useState(
    product?.quantityRule && product.quantityRule?.minQuantity
      ? product.quantityRule.minQuantity
      : quantity
  );

  const [count, setCount] = useState(1);

  const handleIncrement = () => {
    setCount((prevCount) => prevCount + 1);
  };

  const handleDecrement = () => {
    setCount((prevCount) => (prevCount > 1 ? prevCount - 1 : 1));
  };

  useEffect(() => {
    if (visibleEventMessages && visibleEventMessages.some((msg) => msg.addToCartDisabledIfVisible))
      setDisableButtonsByVisibleMessages(true);
    else setDisableButtonsByVisibleMessages(false);
  }, [visibleEventMessages]);

  const handleAddToCart = () => {
    setQuantity(count);
    if (isMandatoryPD && pdValue < 1) {
      setIsPDStartedFromCart(true);
      tryOnRef?.current?.setVisible?.(true);
      tryOnRef?.current?.changeMode?.(TryOnMode.PDTool);
      setTryOnMode(TryOnMode.PDTool);
      return;
    }

    const cartMessage = eventMessages?.find((message) => message.eventID === 4);
    if (cartMessage && cartMessage.visible && !isDraftEditor && !isEditorMode)
      showDialog(
        'question',
        <QuestionDialog
          alignButtons="center"
          eventMessage={cartMessage?.description}
          buttonNoLabel={T._('Cancel', 'Composer')}
          buttonYesLabel={T._('Add to cart', 'Composer')}
          onYesClick={() => {
            if (nftSettings && nftSettings.isNFTEnabled && !isDraftEditor)
              showDialog(
                'nft',
                <NftDialog
                  nftTitle={T._(
                    "You're purchasing a customized product together with an NFT.",
                    'Composer'
                  )}
                  nftMessage={T._(
                    'To confirm and mint your NFT you need an active wallet compatible with Ethereum. Confirm and add your email and wallet address.',
                    'Composer'
                  )}
                  price={nftSettings.priceToAdd + price}
                  buttonNoLabel={T._('Skip and continue', 'Composer')}
                  buttonYesLabel={T._('Confirm and Purchase', 'Composer')}
                  onYesClick={(nftForm: NftForm) => {
                    closeDialog('nft');
                    addToCart([], undefined, useLegacyScreenshot, nftForm);
                  }}
                  onNoClick={() => {
                    closeDialog('nft');
                    addToCart([], undefined, useLegacyScreenshot);
                  }}
                />
              );
            else addToCart([], undefined, useLegacyScreenshot);
            closeDialog('question');
          }}
        />
      );
    else if (nftSettings && nftSettings.isNFTEnabled && !isDraftEditor)
      showDialog(
        'nft',
        <NftDialog
          nftTitle={T._("You're purchasing a customized product together with an NFT.", 'Composer')}
          nftMessage={T._(
            'To confirm and mint your NFT you need an active wallet compatible with Ethereum. Confirm and add your email and wallet address.',
            'Composer'
          )}
          price={nftSettings.priceToAdd + price}
          buttonNoLabel={T._('Skip and continue', 'Composer')}
          buttonYesLabel={T._('Confirm and Purchase', 'Composer')}
          onYesClick={(nftForm: NftForm) => {
            closeDialog('nft');
            addToCart([], undefined, useLegacyScreenshot, nftForm);
          }}
          onNoClick={() => {
            closeDialog('nft');
            addToCart([], undefined, useLegacyScreenshot);
          }}
        />
      );
    else {
      addToCart([], undefined, useLegacyScreenshot);
    }
  };

  const isBuyVisibleForQuoteRule = product?.quoteRule ? product.quoteRule.allowAddToCart : true;
  const { items, removeItem } = useZakeke();

  const handleReset = () => {
    reset();
    removeItemsForStyle(items, removeItem);
    setOpenGroup?.(null);
  };

  return (
    <div>
      <div className="menu_price">
        <div className="menu-price-inner">
          {price !== null && price > 0 && (!sellerSettings || !sellerSettings.hidePrice) && (
            <PriceContainer className="price-container">
              {!isOutOfStock && priceFormatter.format(price)}
              {sellerSettings && sellerSettings.priceInfoText && (
                <p
                  className="price-info"
                  dangerouslySetInnerHTML={{ __html: sellerSettings.priceInfoText }}
                />
              )}
            </PriceContainer>
          )}
          <div className="">
            <p onClick={handleReset} className="start-over">
              START OVER
            </p>
          </div>
        </div>
      </div>
      <div className="menu_footer" ref={viewFooter}>
        <div className="menu_actions">
          <div className="counter-button">
            <span className="counter-value">{count}</span>
            <div className="arrow-container">
              <span className="arrow up" onClick={handleIncrement} />
              <span className="arrow down" onClick={handleDecrement} />
            </div>
          </div>

          {/* {isBuyVisibleForQuoteRule && !isViewerMode && ( */}
          <AddToCartButton
            className="btn btn-primary menu_btn_cart"
            ref={addToCartButtonRef}
            onPointerEnter={() => {
              if (isOutOfStock) openOutOfStockTooltip(addToCartButtonRef.current!, 'top', 'top');
            }}
            onPointerLeave={() => {
              closeOutOfStockTooltip();
            }}
            disabled={disableButtonsByVisibleMessages || isAddToCartLoading || isOutOfStock}
            primary
            onClick={!isAddToCartLoading ? () => handleAddToCart() : () => null}
          >
            {isAddToCartLoading && (
              <div className="flex items-center gap-2 textWhite">
                <span>Loading...</span>
              </div>
            )}
            {!isAddToCartLoading && !isOutOfStock && (
              <span>
                {isDraftEditor || isEditorMode
                  ? T._('Save', 'Composer')
                  : T._('ADD TO BASKET', 'Composer')}
              </span>
            )}
            {!isAddToCartLoading && isOutOfStock && <span>{T._('OUT OF STOCK', 'Composer')}</span>}
          </AddToCartButton>
        </div>
      </div>
    </div>
  );
};
export default MenuFooter;
